import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { trackGAPageView } from '@/hooks/useGoogleAnalytics';

// Generate a unique session ID for the browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('pv_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('pv_session_id', sessionId);
  }
  return sessionId;
};

// Generate a unique visitor ID
const getVisitorId = () => {
  let visitorId = sessionStorage.getItem('pv_visitor_id');
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    sessionStorage.setItem('pv_visitor_id', visitorId);
  }
  return visitorId;
};

let presenceChannel: RealtimeChannel | null = null;

export const usePageTracking = () => {
  const location = useLocation();

  // Set up presence tracking once
  useEffect(() => {
    const visitorId = getVisitorId();
    
    presenceChannel = supabase.channel('visitor_presence', {
      config: {
        presence: {
          key: visitorId,
        },
      },
    });

    presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await presenceChannel?.track({
          online_at: new Date().toISOString(),
          path: location.pathname,
        });
      }
    });

    return () => {
      presenceChannel?.unsubscribe();
      presenceChannel = null;
    };
  }, []);

  // Track page views and update presence path
  useEffect(() => {
    const trackPageView = async () => {
      try {
        const sessionId = getSessionId();
        const referrer = document.referrer || null;
        
        await supabase.functions.invoke('track-pageview', {
          body: {
            path: location.pathname,
            referrer,
            sessionId,
          },
        });

        // Update presence with new path
        if (presenceChannel) {
          await presenceChannel.track({
            online_at: new Date().toISOString(),
            path: location.pathname,
          });
        }
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('Page tracking failed:', error);
      }
    };

    trackPageView();
    trackGAPageView(location.pathname);
  }, [location.pathname]);
};